"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Field,
  TextInput,
  NumberInput,
  SelectInput,
  ResultBanner,
  PrintButton,
  CopyButton,
  PrintFooter,
  DataReviewedNote,
  trackToolResult,
  type Verdict,
} from "@/components/tools/ui";

/* ── Sling angle / leg tension ─────────────────────────────────────────────
   Two independent, industry-standard methods, shown side by side:

   1. TRIGONOMETRIC LEG TENSION (the physics).
      For a symmetrical multi-leg sling the load is taken by TWO legs.
      With included angle θ between opposite legs, each leg's vertical share
      is W / n_eff (n_eff = 2 effective legs). The leg runs at θ/2 from the
      vertical, so the tension along the leg is:
          tension per leg = (W / n_eff) / cos(θ/2)
      The tension factor TF = 1 / cos(θ/2):
          30° → 1.04   60° → 1.16   90° → 1.41   120° → 2.00
      At 120° each leg carries the full weight of the load; beyond ~150° a
      single leg carries more than the whole load. (LEEA / CPCS / BS 7121.)

   2. MODE-FACTOR (uniform-load) METHOD — the rating check riggers use.
      BS EN 1492-1 (textile) / BS EN 818-4 (chain): assembly WLL is derived
      from the SINGLE-LEG WLL × a mode factor M for the configuration:
          single leg, straight ............. 1.0
          2-leg, 0–90° included (0–45° β) .. 1.4
          2-leg, 90–120° included (45–60° β) 1.0
          2-leg basket (parallel legs) ..... 2.0
      3- and 4-leg slings are conventionally rated the SAME as 2-leg, because
      manufacturing tolerance and an offset centre of gravity mean only two
      legs can be relied on to share the load. Required single-leg WLL for a
      lift = W / M. Above 120° included angle is NOT PERMITTED.

   n_eff = 2 for both methods; 1-leg uses the straight pull (no angle). */

type Unit = "kg" | "t";
type Legs = "1" | "2" | "3-4";

/* Mode factor for the rating check (uniform-load method). */
function modeFactor(legs: Legs, includedDeg: number): number {
  if (legs === "1") return 1;
  // 2-leg and 3-4-leg share the same factor band.
  return includedDeg <= 90 ? 1.4 : 1.0;
}

/* Effective load-bearing legs for the trig tension. Always 2 for a
   multi-leg sling (only two legs are relied on); 1 for a single leg. */
function effectiveLegs(legs: Legs): number {
  return legs === "1" ? 1 : 2;
}

const ANGLE_PRESETS: { label: string; value: string }[] = [
  { label: "Tight — under 45°", value: "40" },
  { label: "Good — 45–60°", value: "60" },
  { label: "Acceptable — 60–90°", value: "90" },
  { label: "Maximum — 90–120°", value: "110" },
];

/* Reference table for the explainer-style strip in the result. */
const TENSION_TABLE: { deg: number; tf: number }[] = [
  { deg: 30, tf: 1.04 },
  { deg: 60, tf: 1.16 },
  { deg: 90, tf: 1.41 },
  { deg: 120, tf: 2.0 },
];

function tensionFactor(includedDeg: number): number {
  const half = (includedDeg / 2) * (Math.PI / 180);
  return 1 / Math.cos(half);
}

function toKg(value: number, unit: Unit): number {
  return unit === "t" ? value * 1000 : value;
}

/* ── Inline SVG diagram ────────────────────────────────────────────────────
   Two legs from a single hook down to the load, opening to the included
   angle. The angle (and therefore the spread) updates live. */
function SlingDiagram({ includedDeg, danger }: { includedDeg: number; danger: boolean }) {
  const W = 260;
  const H = 200;
  const hookX = W / 2;
  const hookY = 26;
  const loadY = 150;
  const half = (includedDeg / 2) * (Math.PI / 180);
  const legLen = loadY - hookY;
  // horizontal spread of each leg's foot from the hook centre-line
  const dx = Math.tan(half) * legLen;
  const leftX = hookX - dx;
  const rightX = hookX + dx;
  const loadW = Math.max(70, dx * 2 + 36);
  const loadX = hookX - loadW / 2;
  const legColour = danger ? "#a31616" : "#0f7a5a";
  // arc to annotate the included angle near the hook
  const arcR = 34;
  const ax1 = hookX - Math.sin(half) * arcR;
  const ay1 = hookY + Math.cos(half) * arcR;
  const ax2 = hookX + Math.sin(half) * arcR;
  const ay2 = hookY + Math.cos(half) * arcR;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="img" aria-label={`Two sling legs at an included angle of ${Math.round(includedDeg)} degrees`}>
      {/* hook */}
      <path d={`M ${hookX} 8 v 14`} stroke="#1f2a37" strokeWidth="3" fill="none" strokeLinecap="round" />
      <circle cx={hookX} cy={hookY} r="4" fill="#1f2a37" />
      {/* legs */}
      <line x1={hookX} y1={hookY} x2={leftX} y2={loadY} stroke={legColour} strokeWidth="3.5" strokeLinecap="round" />
      <line x1={hookX} y1={hookY} x2={rightX} y2={loadY} stroke={legColour} strokeWidth="3.5" strokeLinecap="round" />
      {/* vertical reference (dashed) */}
      <line x1={hookX} y1={hookY} x2={hookX} y2={loadY} stroke="#9aa6b2" strokeWidth="1" strokeDasharray="3 3" />
      {/* included-angle arc + label */}
      <path d={`M ${ax1} ${ay1} A ${arcR} ${arcR} 0 0 0 ${ax2} ${ay2}`} fill="none" stroke={legColour} strokeWidth="1.5" />
      <text x={hookX} y={hookY + arcR + 14} textAnchor="middle" fontSize="13" fontWeight="700" fill={legColour}>
        {Math.round(includedDeg)}°
      </text>
      {/* load */}
      <rect x={loadX} y={loadY} width={loadW} height="30" rx="4" fill="#1f2a37" />
      <text x={hookX} y={loadY + 20} textAnchor="middle" fontSize="12" fontWeight="600" fill="#ffffff">
        LOAD
      </text>
    </svg>
  );
}

export function SlingAngleCalculator() {
  const [unit, setUnit] = useState<Unit>("kg");
  const [loadStr, setLoadStr] = useState("");
  const [legs, setLegs] = useState<Legs>("2");
  const [angleStr, setAngleStr] = useState("60");
  const [wllStr, setWllStr] = useState(""); // marked WLL per leg, in kg
  const [reference, setReference] = useState("");
  const resultTracked = useRef(false);

  const angle = useMemo(() => {
    const a = parseFloat(angleStr);
    if (!isFinite(a)) return null;
    return Math.min(180, Math.max(0, a));
  }, [angleStr]);

  const computed = useMemo(() => {
    const loadVal = parseFloat(loadStr);
    if (!isFinite(loadVal) || loadVal <= 0) return null;
    const loadKg = toKg(loadVal, unit);
    const nEff = effectiveLegs(legs);

    // Single leg: straight pull, no angle effect.
    const includedDeg = legs === "1" ? 0 : angle ?? 0;
    const tf = legs === "1" ? 1 : tensionFactor(includedDeg);
    // Trigonometric tension on the worst-loaded leg.
    const legTensionKg = (loadKg / nEff) * tf;

    // Mode-factor rating check.
    const M = modeFactor(legs, includedDeg);
    // Minimum single-leg WLL the assembly needs for this lift.
    const requiredSingleLegKg = loadKg / M;

    // Marked WLL comparison (per leg).
    const wllVal = parseFloat(wllStr);
    const wllKg = isFinite(wllVal) && wllVal > 0 ? toKg(wllVal, unit) : null;
    // Two independent checks against the marked single-leg WLL:
    //  - trig:  actual leg tension vs marked WLL
    //  - rating: required single-leg WLL (W / M) vs marked WLL
    const trigPct = wllKg ? (legTensionKg / wllKg) * 100 : null;
    const ratingPct = wllKg ? (requiredSingleLegKg / wllKg) * 100 : null;
    // Governing (higher) utilisation drives the verdict colour.
    const govPct =
      trigPct !== null && ratingPct !== null ? Math.max(trigPct, ratingPct) : null;

    const overMaxAngle = legs !== "1" && includedDeg > 120;

    let level: Verdict;
    let title: string;
    let advice: string;

    if (overMaxAngle) {
      level = "veryhigh";
      title = `${Math.round(includedDeg)}° included — over the 120° maximum`;
      advice =
        "An included angle above 120° is not permitted: each leg is then carrying more than the whole load. Use longer slings, a wider hook arrangement, or a spreader/lifting beam to bring the angle down before lifting.";
    } else if (govPct !== null && govPct > 100) {
      level = "veryhigh";
      title = `Leg load exceeds the marked WLL`;
      advice =
        "On these figures a leg is loaded beyond its marked Working Load Limit. Do not lift. Reduce the load, add legs, or reduce the included angle — then re-check against the sling's own marking.";
    } else if (govPct !== null && govPct > 80) {
      level = "high";
      title = `Leg load is close to the marked WLL`;
      advice =
        "The worst-loaded leg is within 20% of its marked Working Load Limit. Treat this as a tight lift — confirm the load weight, the centre of gravity and the angle on site, and consider more margin.";
    } else if (legs !== "1" && includedDeg > 90) {
      level = "medium";
      title = `${Math.round(includedDeg)}° included — above the recommended 90°`;
      advice =
        "Within the 120° limit but above the recommended 90° working maximum. Tension rises steeply here; the sling's rated capacity (mode factor) drops to single-leg. Tighten the angle where you can.";
    } else {
      level = "low";
      title = wllKg
        ? `Leg load within the marked WLL`
        : `${Math.round(includedDeg)}° included — within recommended limits`;
      advice =
        "Within recommended working limits on these figures. The result is a planning aid only — the sling's own marking and the appointed person's lift plan govern the actual lift.";
    }

    return {
      loadKg,
      nEff,
      includedDeg,
      tf,
      legTensionKg,
      M,
      requiredSingleLegKg,
      wllKg,
      trigPct,
      ratingPct,
      govPct,
      overMaxAngle,
      level,
      title,
      advice,
    };
  }, [loadStr, unit, legs, angle, wllStr]);

  useEffect(() => {
    if (computed && !resultTracked.current) {
      resultTracked.current = true;
      trackToolResult("sling-angle-calculator", {
        legTension: Math.round(computed.legTensionKg),
        angle: Math.round(computed.includedDeg),
      });
    }
  }, [computed]);

  // Display a kg value back in the chosen unit.
  const showWeight = (kg: number): string => {
    if (unit === "t") return `${(kg / 1000).toLocaleString("en-GB", { maximumFractionDigits: 3 })} t`;
    return `${Math.round(kg).toLocaleString("en-GB")} kg`;
  };

  const pctClass = (pct: number) =>
    pct > 100 ? "text-risk-veryhigh" : pct > 80 ? "text-[#b34e10]" : "text-emerald-deep";

  const danger = computed ? computed.overMaxAngle || (computed.govPct !== null && computed.govPct > 100) : false;

  const buildCopyText = (): string => {
    if (!computed) return "";
    const lines: string[] = [];
    lines.push("SLING ANGLE / LEG TENSION — planning aid");
    if (reference.trim()) lines.push(`Lift / reference: ${reference.trim()}`);
    lines.push(
      `Date: ${new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}`,
    );
    lines.push("");
    lines.push(`Load: ${showWeight(computed.loadKg)}`);
    lines.push(
      `Slinging: ${legs === "1" ? "single leg (straight)" : legs === "2" ? "2-leg" : "3/4-leg (rated as 2-leg)"}`,
    );
    if (legs !== "1") lines.push(`Included angle between legs: ${Math.round(computed.includedDeg)}°`);
    lines.push("");
    lines.push(
      `Tension on worst-loaded leg: ${showWeight(computed.legTensionKg)} (tension factor ${computed.tf.toFixed(2)})`,
    );
    lines.push(
      `Mode-factor rating check: M = ${computed.M.toFixed(1)} -> minimum single-leg WLL needed = ${showWeight(computed.requiredSingleLegKg)}`,
    );
    if (computed.wllKg !== null) {
      lines.push(`Marked WLL per leg: ${showWeight(computed.wllKg)}`);
      lines.push(`Leg tension vs marked WLL: ${Math.round(computed.trigPct!)}%`);
      lines.push(`Required WLL vs marked WLL: ${Math.round(computed.ratingPct!)}%`);
    }
    lines.push("");
    lines.push(`Verdict: ${computed.title}`);
    lines.push(computed.advice);
    lines.push("");
    lines.push(
      "Planning aid only. The sling's own marking and the appointed person's lift plan govern. Check the tag is legible and the accessory is in date for LOLER thorough examination before use.",
    );
    lines.push("Generated with the free sling angle calculator at ramsdocs.co.uk");
    return lines.join("\n");
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
      {/* Inputs */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">Lift details</h2>
        <p className="mt-1 text-sm text-muted">
          Enter the load and how it&apos;s slung. As the legs spread, the tension in each one rises far faster
          than most people expect.
        </p>

        <div className="mt-5 space-y-5">
          {/* Load + unit */}
          <Field label="Load weight" hint="The total weight being lifted, including any lifting frame or attachments.">
            <div className="grid grid-cols-[1fr_auto] gap-2">
              <NumberInput
                min={0}
                step={unit === "t" ? 0.1 : 10}
                value={loadStr}
                onChange={(e) => setLoadStr(e.target.value)}
                placeholder={unit === "t" ? "e.g. 1.5" : "e.g. 1500"}
                aria-label="Load weight"
              />
              <SelectInput
                aria-label="Weight unit"
                value={unit}
                onChange={(e) => setUnit(e.target.value as Unit)}
                className="w-20"
              >
                <option value="kg">kg</option>
                <option value="t">t</option>
              </SelectInput>
            </div>
          </Field>

          {/* Number of legs */}
          <Field
            label="Number of sling legs"
            hint="3- and 4-leg slings are conventionally rated as 2 legs — an offset centre of gravity means only two legs can be relied on to share the load."
          >
            <SelectInput value={legs} onChange={(e) => setLegs(e.target.value as Legs)}>
              <option value="1">1 leg (straight / vertical)</option>
              <option value="2">2 legs</option>
              <option value="3-4">3 or 4 legs (rated as 2)</option>
            </SelectInput>
          </Field>

          {/* Angle (hidden for single leg) */}
          {legs !== "1" && (
            <Field
              label="Included angle between the legs"
              hint="The angle measured at the top, between opposite legs. Lower is safer; never exceed 120°."
            >
              <div className="space-y-3">
                <SelectInput
                  aria-label="Angle preset"
                  value=""
                  onChange={(e) => {
                    if (e.target.value) setAngleStr(e.target.value);
                  }}
                >
                  <option value="">Choose a preset…</option>
                  {ANGLE_PRESETS.map((p) => (
                    <option key={p.label} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </SelectInput>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min={0}
                    max={150}
                    step={1}
                    value={angle ?? 60}
                    onChange={(e) => setAngleStr(e.target.value)}
                    className="h-2 flex-1 cursor-pointer accent-emerald"
                    aria-label="Included angle slider"
                  />
                  <div className="flex w-24 items-center gap-1">
                    <NumberInput
                      min={0}
                      max={180}
                      step={1}
                      value={angleStr}
                      onChange={(e) => setAngleStr(e.target.value)}
                      aria-label="Included angle in degrees"
                    />
                    <span className="text-sm text-muted">°</span>
                  </div>
                </div>
              </div>
            </Field>
          )}

          {/* Marked WLL (optional) */}
          <Field
            label={`Sling marked WLL per leg (optional, ${unit})`}
            hint="The Working Load Limit stamped on the sling's tag, per single leg. Leave blank to see leg tension only."
          >
            <NumberInput
              min={0}
              step={unit === "t" ? 0.1 : 10}
              value={wllStr}
              onChange={(e) => setWllStr(e.target.value)}
              placeholder={unit === "t" ? "e.g. 1.0" : "e.g. 1000"}
            />
          </Field>

          {/* Reference for the record */}
          <Field label="Lift / reference (for the record)" hint="Optional — appears on the printed and copied record.">
            <TextInput
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. Plant room AHU lift — 12 June"
            />
          </Field>
        </div>

        {/* Live diagram */}
        {legs !== "1" && (
          <div className="mt-6 rounded-xl border border-line bg-mist/60 p-4">
            <div className="text-[0.78rem] font-bold uppercase tracking-wide text-muted">Included angle</div>
            <div className="mx-auto mt-2 max-w-[280px]">
              <SlingDiagram includedDeg={computed ? computed.includedDeg : angle ?? 60} danger={danger} />
            </div>
            <p className="mt-1 text-center text-[0.78rem] text-muted">
              Wider legs = sharper angle = far higher tension in each leg.
            </p>
          </div>
        )}
      </div>

      {/* Result + printable record */}
      <div className="space-y-4">
        {computed ? (
          <>
            <div className="print-area card shadow-soft p-6" id="sling-record">
              <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
                Sling angle / leg tension record
              </div>
              <h2 className="mt-1 text-lg">
                {reference.trim() ? `${reference.trim()} — ` : ""}
                {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
              </h2>

              <div className="mt-4">
                <ResultBanner level={computed.level} title={computed.title}>
                  {computed.advice}
                </ResultBanner>
              </div>

              {/* Headline numbers */}
              <div className="mt-4 grid grid-cols-2 gap-2 text-center text-[0.78rem]">
                <div className="rounded-lg bg-mist px-2 py-2">
                  <div className="font-display text-lg font-bold text-ink">{showWeight(computed.legTensionKg)}</div>
                  <div className="text-muted">tension per leg</div>
                </div>
                <div className="rounded-lg bg-mist px-2 py-2">
                  <div className="font-display text-lg font-bold text-ink">
                    {computed.tf.toFixed(2)}×
                  </div>
                  <div className="text-muted">tension factor</div>
                </div>
              </div>

              {/* Per-leg WLL utilisation */}
              {computed.wllKg !== null && (
                <div className="mt-3 space-y-2">
                  <div className="flex items-center justify-between rounded-lg border border-line bg-white px-3 py-2 text-sm">
                    <span className="text-muted">Leg tension vs marked WLL</span>
                    <span className={`font-bold ${pctClass(computed.trigPct!)}`}>
                      {Math.round(computed.trigPct!)}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between rounded-lg border border-line bg-white px-3 py-2 text-sm">
                    <span className="text-muted">Required WLL (mode factor) vs marked</span>
                    <span className={`font-bold ${pctClass(computed.ratingPct!)}`}>
                      {Math.round(computed.ratingPct!)}%
                    </span>
                  </div>
                </div>
              )}

              {/* Working detail table */}
              <table className="mt-4 w-full text-sm">
                <tbody>
                  <tr className="border-b border-line/60">
                    <td className="py-2 pr-2 text-muted">Load</td>
                    <td className="py-2 text-right font-semibold text-ink">{showWeight(computed.loadKg)}</td>
                  </tr>
                  <tr className="border-b border-line/60">
                    <td className="py-2 pr-2 text-muted">Effective load-bearing legs</td>
                    <td className="py-2 text-right font-semibold text-ink">{computed.nEff}</td>
                  </tr>
                  {legs !== "1" && (
                    <tr className="border-b border-line/60">
                      <td className="py-2 pr-2 text-muted">Included angle</td>
                      <td className="py-2 text-right font-semibold text-ink">{Math.round(computed.includedDeg)}°</td>
                    </tr>
                  )}
                  <tr className="border-b border-line/60">
                    <td className="py-2 pr-2 text-muted">Mode factor (rating)</td>
                    <td className="py-2 text-right font-semibold text-ink">{computed.M.toFixed(1)}</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-2 text-muted">Min. single-leg WLL needed</td>
                    <td className="py-2 text-right font-semibold text-ink">
                      {showWeight(computed.requiredSingleLegKg)}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Reference tension-factor strip */}
              <div className="mt-4">
                <div className="text-[0.72rem] font-semibold uppercase tracking-wide text-muted">
                  How tension grows with the angle
                </div>
                <div className="mt-1.5 grid grid-cols-4 gap-1.5 text-center text-[0.74rem]">
                  {TENSION_TABLE.map((t) => (
                    <div
                      key={t.deg}
                      className={`rounded-lg px-1 py-1.5 ${
                        legs !== "1" && Math.abs(computed.includedDeg - t.deg) <= 15
                          ? "bg-emerald-tint text-emerald-deep"
                          : "bg-mist text-ink"
                      }`}
                    >
                      <div className="font-bold">{t.deg}°</div>
                      <div className="text-muted">×{t.tf.toFixed(2)}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Hard warnings */}
              <div className="mt-4 rounded-lg border border-risk-high/30 bg-[#fef0e6] px-3 py-2.5 text-[0.8rem] leading-relaxed text-[#b34e10]">
                <div className="font-bold">Before you lift</div>
                <ul className="mt-1 list-disc space-y-0.5 pl-4">
                  <li>Never exceed a 120° included angle — at 120° each leg already carries the full load, and by ~150° a single leg carries more than the whole load.</li>
                  <li>An offset centre of gravity loads the legs unevenly — the worst leg can carry far more than this even share.</li>
                  <li>The tag must be legible and the WLL marking present; check the configuration matches.</li>
                  <li>The accessory must be in date for LOLER thorough examination (6-monthly for accessories) and free of damage.</li>
                </ul>
              </div>

              <DataReviewedNote date="6 June 2026">
                Based on the uniform-load (mode-factor) method of BS EN 1492-1 / BS EN 818-4 and BS 7121.
                Always work to the sling&apos;s own marking and the appointed person&apos;s lift plan.
              </DataReviewedNote>

              <PrintFooter toolName="sling angle calculator" />
            </div>
            <div className="flex flex-wrap justify-end gap-3">
              <CopyButton tool="sling-angle-calculator" buildText={buildCopyText} label="Copy record as text" />
              <PrintButton tool="sling-angle-calculator" label="Print / save record as PDF" />
            </div>
          </>
        ) : (
          <div className="card border-dashed p-6 text-center text-sm text-muted">
            Enter a load weight to see the tension in each sling leg, the mode-factor rating check and how the
            included angle affects safety.
          </div>
        )}
      </div>
    </div>
  );
}
